// src/services/emailNotificacaoService.ts
//
// Configuração e disparo de notificações por e-mail transacional via SMTP
// direto (qualquer provedor). Espelha o módulo de notificações via WhatsApp
// (whatsappService.ts): mesmos eventos do sistema, mesmo padrão de opt-in do
// cliente, envio feito pela Edge Function "email-notificar" (chamada
// automaticamente por um trigger do banco quando o status de uma OS muda, ou
// manualmente aqui para testes/reenvios).

import { supabase, supabaseAnonKey } from '../lib/supabase';

export interface ConfiguracaoEmail {
  id: number;
  ativo: boolean;
  remetente_nome: string | null;
  remetente_email: string | null;
  smtp_host: string | null;
  smtp_porta: number | null;
  smtp_seguranca: 'ssl' | 'starttls' | 'nenhuma';
  smtp_usuario: string | null;
  possui_senha_smtp: boolean;
  updated_at: string;
}

export interface NotificacaoEmailTemplate {
  evento_codigo: string;
  descricao: string;
  assunto: string | null;
  corpo_html: string | null;
  ativo: boolean;
  variaveis_ajuda: string | null;
  updated_at: string;
}

export interface NotificacaoEmailLog {
  id: number;
  evento_codigo: string;
  pessoa_id: number | null;
  os_id: number | null;
  email: string;
  assunto: string | null;
  status: string;
  erro_detalhe: string | null;
  created_at: string;
  updated_at: string;
}

async function invocarFuncao<T = any>(body: Record<string, unknown>): Promise<T> {
  // Mesmo motivo documentado em src/lib/fiscalApi.ts e whatsappService.ts:
  // sem sessão do Supabase Auth (este app usa Firebase Auth), o Authorization
  // precisa ser explícito ou o próprio gateway barra a chamada com 401.
  const { data, error } = await supabase.functions.invoke('email-notificar', {
    body,
    headers: { Authorization: `Bearer ${supabaseAnonKey}` },
  });
  if (error) {
    throw new Error(`Falha ao chamar email-notificar: ${error.message}`);
  }
  return data as T;
}

export const emailNotificacaoService = {
  async getConfiguracao(): Promise<ConfiguracaoEmail> {
    const { data, error } = await supabase
      .from('configuracao_email')
      .select('id, ativo, remetente_nome, remetente_email, smtp_host, smtp_porta, smtp_seguranca, smtp_usuario, smtp_senha_vault_id, updated_at')
      .eq('id', 1)
      .maybeSingle();

    if (error) throw new Error(error.message);

    return {
      id: data?.id ?? 1,
      ativo: data?.ativo ?? false,
      remetente_nome: data?.remetente_nome ?? null,
      remetente_email: data?.remetente_email ?? null,
      smtp_host: data?.smtp_host ?? null,
      smtp_porta: data?.smtp_porta ?? 587,
      smtp_seguranca: (data?.smtp_seguranca as ConfiguracaoEmail['smtp_seguranca']) ?? 'starttls',
      smtp_usuario: data?.smtp_usuario ?? null,
      possui_senha_smtp: !!data?.smtp_senha_vault_id,
      updated_at: data?.updated_at ?? new Date().toISOString(),
    };
  },

  async salvarConfiguracao(params: {
    ativo: boolean;
    remetente_nome: string;
    remetente_email: string;
    smtp_host: string;
    smtp_porta: number;
    smtp_seguranca: 'ssl' | 'starttls' | 'nenhuma';
    smtp_usuario: string;
  }): Promise<void> {
    const { error } = await supabase
      .from('configuracao_email')
      .update({
        ativo: params.ativo,
        remetente_nome: params.remetente_nome || null,
        remetente_email: params.remetente_email || null,
        smtp_host: params.smtp_host || null,
        smtp_porta: params.smtp_porta || 587,
        smtp_seguranca: params.smtp_seguranca || 'starttls',
        smtp_usuario: params.smtp_usuario || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', 1);

    if (error) throw new Error(error.message);
  },

  /** Salva a senha SMTP no Vault via RPC — nunca fica em texto puro numa coluna. */
  async salvarSenhaSmtp(valor: string): Promise<void> {
    const { error } = await supabase.rpc('salvar_senha_smtp_email', { p_valor: valor });
    if (error) throw new Error(error.message);
  },

  async getTemplates(): Promise<NotificacaoEmailTemplate[]> {
    const { data, error } = await supabase
      .from('notificacao_email_template')
      .select('*')
      .order('evento_codigo', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as NotificacaoEmailTemplate[]) || [];
  },

  async salvarTemplate(evento_codigo: string, params: { assunto: string; corpo_html: string; ativo: boolean }): Promise<void> {
    const { error } = await supabase
      .from('notificacao_email_template')
      .update({
        assunto: params.assunto || null,
        corpo_html: params.corpo_html || null,
        ativo: params.ativo,
        updated_at: new Date().toISOString(),
      })
      .eq('evento_codigo', evento_codigo);
    if (error) throw new Error(error.message);
  },

  async getLogs(limit = 50): Promise<NotificacaoEmailLog[]> {
    const { data, error } = await supabase
      .from('notificacao_email_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data as NotificacaoEmailLog[]) || [];
  },

  async testarConexao(emailTeste?: string): Promise<{ sucesso: boolean; mensagem: string }> {
    return invocarFuncao({ operacao: 'testar_conexao', email_teste: emailTeste });
  },

  async reenviarNotificacao(logId: number): Promise<{ sucesso: boolean; mensagem: string }> {
    return invocarFuncao({ operacao: 'reenviar', log_id: logId });
  },

  async notificarEvento(evento_codigo: string, os_id: number): Promise<{ sucesso: boolean; mensagem: string }> {
    return invocarFuncao({ operacao: 'notificar', evento_codigo, os_id });
  },
};
